import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import MainNavigation from '../../components/ui/MainNavigation';
import FooterSection from '../homepage/components/FooterSection';
import NavigationBreadcrumb from '../../components/ui/NavigationBreadcrumb';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import { supabase } from '../../lib/supabase';
import ProductsTab from './components/ProductsTab';
import ReviewsTab from './components/ReviewsTab';
import NewsTab from './components/NewsTab';

const ProviderProfilePage = () => {
  const { providerId } = useParams();
  const [provider, setProvider] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');

  const breadcrumbItems = [
    { label: 'Home', path: '/homepage' },
    { label: 'Aanbieders', path: '/leveranciers' },
    { label: provider?.full_name || 'Profiel', path: `/provider-profile/${providerId}` }
  ];

  useEffect(() => {
    if (providerId) {
      fetchProviderData();
    }
  }, [providerId]);

  const fetchProviderData = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data: providerData, error: providerError } = await supabase
        ?.from('user_profiles')
        ?.select(`
          *,
          provider_service_areas(*),
          provider_certifications(*)
        `)
        ?.eq('id', providerId)
        ?.eq('user_type', 'provider')
        ?.single();

      if (providerError) throw providerError;

      // Fetch completed projects count
      const { count } = await supabase
        ?.from('quote_requests')
        ?.select('*', { count: 'exact', head: true })
        ?.eq('provider_id', providerId)
        ?.eq('status', 'completed');

      setProvider({
        ...providerData,
        completedProjects: count || 0,
        rating: (Math.random() * 1.5 + 3.5)?.toFixed(1),
        reviewCount: Math.floor(Math.random() * 300) + 50,
        yearsExperience: Math.floor(Math.random() * 15) + 5
      });
    } catch (err) {
      console.error('Error fetching provider:', err);
      setError(err?.message || 'Er is een fout opgetreden');
    } finally {
      setLoading(false);
    }
  };

  const renderStars = (rating) => {
    const numRating = parseFloat(rating);
    return (
      <div className="flex items-center space-x-1">
        {[1, 2, 3, 4, 5]?.map((star) => (
          <Icon
            key={star}
            name="Star"
            size={20}
            className={star <= numRating ? 'text-yellow-500 fill-yellow-500' : 'text-gray-300'}
          />
        ))}
      </div>
    );
  };

  const tabs = [
    { id: 'overview', label: 'Overzicht', icon: 'Home' },
    { id: 'products', label: 'Producten & Oplossingen', icon: 'Package' },
    { id: 'reviews', label: 'Klantbeoordelingen', icon: 'Star' },
    { id: 'news', label: 'Nieuws', icon: 'Newspaper' }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex">
        <MainNavigation />
        <div className="flex-1 lg:ml-[72px] flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Profiel laden...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !provider) {
    return (
      <div className="min-h-screen bg-background flex">
        <MainNavigation />
        <div className="flex-1 lg:ml-[72px] flex items-center justify-center">
          <div className="text-center">
            <Icon name="AlertCircle" size={48} className="text-error mx-auto mb-4" />
            <h2 className="text-xl font-bold text-foreground mb-2">Aanbieder niet gevonden</h2>
            <p className="text-muted-foreground mb-4">{error || 'Deze aanbieder bestaat niet of is niet beschikbaar.'}</p>
            <Link to="/leveranciers">
              <Button>Terug naar overzicht</Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const hasActiveCertifications = provider?.provider_certifications?.some(
    cert => cert?.status === 'active'
  );

  const primaryServiceArea = provider?.provider_service_areas?.find(area => area?.is_primary) ||
                             provider?.provider_service_areas?.[0];

  return (
    <div className="min-h-screen bg-background flex">
      <MainNavigation />
      <div className="flex-1 lg:ml-[72px]">
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-primary/10 via-secondary/5 to-background py-8 sm:py-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <NavigationBreadcrumb items={breadcrumbItems} />
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="mt-6"
            >
              {/* Provider Header */}
              <div className="bg-card border border-border rounded-xl p-6 shadow-elevation-2">
                <div className="flex flex-col lg:flex-row gap-6">
                  {/* Logo/Avatar */}
                  <div className="flex-shrink-0">
                    <div className="w-24 h-24 bg-primary/10 rounded-full flex items-center justify-center">
                      <Icon name="Building2" size={48} className="text-primary" />
                    </div>
                  </div>

                  {/* Provider Info */}
                  <div className="flex-1">
                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-4">
                      <div>
                        <h1 className="text-2xl sm:text-3xl font-heading font-bold text-foreground mb-2">
                          {provider?.full_name}
                        </h1>
                        <div className="flex items-center space-x-3 mb-2">
                          {renderStars(provider?.rating)}
                          <span className="text-lg font-semibold text-foreground">{provider?.rating}</span>
                          <span className="text-sm text-muted-foreground">({provider?.reviewCount} beoordelingen)</span>
                        </div>
                        {hasActiveCertifications && (
                          <div className="flex items-center space-x-2 text-success">
                            <Icon name="BadgeCheck" size={16} />
                            <span className="text-sm font-medium">Geverifieerde aanbieder</span>
                          </div>
                        )}
                      </div>
                      <div className="flex gap-2">
                        <Link to={`/quote-request-form?leverancier=${provider?.id}`}>
                          <Button iconName="MessageSquare" iconPosition="left">
                            Offerte aanvragen
                          </Button>
                        </Link>
                        <Button variant="outline" iconName="Heart" size="icon" title="Bewaar aanbieder" />
                      </div>
                    </div>

                    {/* Key Metrics */}
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                      <div className="bg-background rounded-lg p-3 border border-border">
                        <div className="flex items-center space-x-2 mb-1">
                          <Icon name="Award" size={16} className="text-primary" />
                          <span className="text-xs text-muted-foreground">Ervaring</span>
                        </div>
                        <p className="text-lg font-bold text-foreground">{provider?.yearsExperience} jaar</p>
                      </div>
                      <div className="bg-background rounded-lg p-3 border border-border">
                        <div className="flex items-center space-x-2 mb-1">
                          <Icon name="CheckCircle" size={16} className="text-success" />
                          <span className="text-xs text-muted-foreground">Projecten</span>
                        </div>
                        <p className="text-lg font-bold text-foreground">{provider?.completedProjects}+</p>
                      </div>
                      {primaryServiceArea && (
                        <div className="bg-background rounded-lg p-3 border border-border">
                          <div className="flex items-center space-x-2 mb-1">
                            <Icon name="MapPin" size={16} className="text-primary" />
                            <span className="text-xs text-muted-foreground">Regio</span>
                          </div>
                          <p className="text-sm font-bold text-foreground">{primaryServiceArea?.province}</p>
                        </div>
                      )}
                      <div className="bg-background rounded-lg p-3 border border-border">
                        <div className="flex items-center space-x-2 mb-1">
                          <Icon name="Clock" size={16} className="text-primary" />
                          <span className="text-xs text-muted-foreground">Reactie</span>
                        </div>
                        <p className="text-sm font-bold text-foreground">{'< 24 uur'}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Tabs Navigation */}
        <section className="bg-card border-b border-border sticky top-0 z-10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex space-x-1 overflow-x-auto py-2">
              {tabs?.map((tab) => (
                <button
                  key={tab?.id}
                  onClick={() => setActiveTab(tab?.id)}
                  className={`flex items-center space-x-2 px-4 py-3 rounded-md text-sm font-medium transition-colors whitespace-nowrap ${
                    activeTab === tab?.id
                      ? 'bg-primary text-primary-foreground'
                      : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                  }`}
                >
                  <Icon name={tab?.icon} size={16} />
                  <span>{tab?.label}</span>
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Tab Content */}
        <section className="py-8">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {activeTab === 'overview' && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4 }}
                className="space-y-6"
              >
                {/* About Section */}
                <div className="bg-card border border-border rounded-xl p-6">
                  <h2 className="text-xl font-heading font-bold text-foreground mb-4">Over {provider?.full_name}</h2>
                  <p className="text-muted-foreground leading-relaxed">
                    {provider?.company_description || `${provider?.full_name} is een ervaren aanbieder van duurzame oplossingen met jarenlange expertise in de sector. Wij bieden hoogwaardige producten en diensten voor particulieren en bedrijven die willen verduurzamen.`}
                  </p>
                </div>

                {/* Certifications */}
                {hasActiveCertifications && (
                  <div className="bg-card border border-border rounded-xl p-6">
                    <h2 className="text-xl font-heading font-bold text-foreground mb-4">Certificeringen</h2>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                      {provider?.provider_certifications
                        ?.filter(cert => cert?.status === 'active')
                        ?.map((cert, idx) => (
                          <div key={idx} className="flex items-center space-x-3 p-4 bg-success/5 border border-success/20 rounded-lg">
                            <Icon name="Award" size={24} className="text-success" />
                            <div>
                              <p className="font-semibold text-foreground">{cert?.certification_name}</p>
                              <p className="text-xs text-muted-foreground">Actief</p>
                            </div>
                          </div>
                        ))}
                    </div>
                  </div>
                )}

                {/* Service Areas */}
                {provider?.provider_service_areas?.length > 0 && (
                  <div className="bg-card border border-border rounded-xl p-6">
                    <h2 className="text-xl font-heading font-bold text-foreground mb-4">Werkgebied</h2>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                      {provider?.provider_service_areas?.map((area, idx) => (
                        <div key={idx} className="flex items-start space-x-3 p-4 bg-background border border-border rounded-lg">
                          <Icon name="MapPin" size={20} className="text-primary flex-shrink-0 mt-1" />
                          <div>
                            <p className="font-semibold text-foreground">{area?.province}</p>
                            {area?.service_radius_km && (
                              <p className="text-sm text-muted-foreground">Straal: {area?.service_radius_km} km</p>
                            )}
                            {area?.is_primary && (
                              <span className="inline-block mt-1 px-2 py-0.5 bg-primary/10 text-primary text-xs rounded-full">
                                Primair gebied
                              </span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Contact Info */}
                <div className="bg-card border border-border rounded-xl p-6">
                  <h2 className="text-xl font-heading font-bold text-foreground mb-4">Contact</h2>
                  <div className="space-y-3">
                    {provider?.email && (
                      <div className="flex items-center space-x-3">
                        <Icon name="Mail" size={20} className="text-muted-foreground" />
                        <span className="text-foreground">{provider?.email}</span>
                      </div>
                    )}
                    {provider?.phone_number && (
                      <div className="flex items-center space-x-3">
                        <Icon name="Phone" size={20} className="text-muted-foreground" />
                        <span className="text-foreground">{provider?.phone_number}</span>
                      </div>
                    )}
                  </div>
                </div>
              </motion.div>
            )}

            {activeTab === 'products' && <ProductsTab providerId={providerId} />}
            {activeTab === 'reviews' && <ReviewsTab providerId={providerId} providerName={provider?.full_name} />}
            {activeTab === 'news' && <NewsTab providerId={providerId} />}
          </div>
        </section>

        <FooterSection />
      </div>
    </div>
  );
};

export default ProviderProfilePage;